import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams, Link } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import AppLayout from './components/layout/AppLayout';
import ProtectedRoute from './components/ProtectedRoute';
import Spinner from './components/ui/Spinner';

// ── Public / auth entry screens: eager, they are the first paint ──
import Login from './pages/Login';
import ApplicationForm from './pages/public/ApplicationForm';
import TrackApplication from './pages/public/TrackApplication';
import AcceptInvite from './pages/public/AcceptInvite';
import AdminForgotPassword from './pages/public/AdminForgotPassword';
import AdminResetPassword from './pages/public/AdminResetPassword';
import CandidateForgotPassword from './pages/public/CandidateForgotPassword';
import CandidateResetPassword from './pages/public/CandidateResetPassword';
import CandidateLogin from './pages/public/CandidateLogin';
import CandidatePortal from './pages/public/CandidatePortal';
import CandidateSignup from './pages/public/CandidateSignup';
import CandidateVerifySignup from './pages/public/CandidateVerifySignup';
import CareersPage from './pages/public/CareersPage';

// ── Admin screens: lazy, so recharts / dnd / react-hook-form / date-fns stay
// out of the bundle a visitor downloads before the login screen is usable ──
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Pipeline = lazy(() => import('./pages/pipeline/Pipeline'));
const CandidatesList = lazy(() => import('./pages/candidates/CandidatesList'));
const CandidateDetail = lazy(() => import('./pages/candidates/CandidateDetail'));
const Assessments = lazy(() => import('./pages/Assessments'));
const Settings = lazy(() => import('./pages/Settings'));
const Users = lazy(() => import('./pages/Users'));
const EmailTemplates = lazy(() => import('./pages/EmailTemplates'));
const RolesPermissions = lazy(() => import('./pages/RolesPermissions'));
const CareerSettingsPage = lazy(() => import('./pages/CareerSettingsPage'));
const TalentPool = lazy(() => import('./pages/TalentPool'));
const HelpManuals = lazy(() => import('./pages/HelpManuals'));
const ImportCandidates = lazy(() => import('./pages/ImportCandidates'));

function RedirectToAdminCandidate() {
  const { id } = useParams();
  return <Navigate to={`/admin/candidates/${id}`} replace />;
}

function RedirectIfAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Spinner fullPage />;
  if (isAuthenticated) return <Navigate to="/admin/dashboard" replace />;
  return children;
}

// Unknown paths used to land on the admin dashboard, which bounced a candidate
// who mistyped /careers onto an admin login.
function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
        <p className="text-4xl font-bold text-slate-300">404</p>
        <h1 className="mt-2 text-lg font-semibold text-slate-900">This page does not exist</h1>
        <p className="mt-2 text-sm text-slate-600">Check the address, or pick up where you meant to go:</p>
        <div className="mt-6 flex flex-col gap-2 text-sm">
          <Link to="/careers" className="text-slate-900 font-medium hover:underline">Browse open roles</Link>
          <Link to="/track" className="text-slate-900 font-medium hover:underline">Track your application</Link>
          <Link to="/candidate/login" className="text-slate-900 font-medium hover:underline">Candidate sign in</Link>
          <Link to="/admin/login" className="text-slate-600 hover:underline">Staff sign in</Link>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    // Suspense has to sit above every route that renders a lazy element.
    <Suspense fallback={<Spinner fullPage />}>
      <Routes>
        {/* Public routes */}
        <Route path="/admin/login" element={<RedirectIfAuth><Login /></RedirectIfAuth>} />
        <Route path="/login" element={<Navigate to="/admin/login" replace />} />
        <Route path="/apply" element={<ApplicationForm />} />
        <Route path="/track" element={<TrackApplication />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/admin/forgot-password" element={<RedirectIfAuth><AdminForgotPassword /></RedirectIfAuth>} />
        <Route path="/admin/reset-password" element={<AdminResetPassword />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/candidate/login" element={<CandidateLogin />} />
        <Route path="/candidate/forgot-password" element={<CandidateForgotPassword />} />
        <Route path="/candidate/reset-password" element={<CandidateResetPassword />} />
        <Route path="/candidate/signup" element={<CandidateSignup />} />
        {/* Target of the signup verification link mailed by /api/auth/candidate-signup. */}
        <Route path="/candidate/verify-signup" element={<CandidateVerifySignup />} />
        <Route path="/portal" element={<CandidatePortal />} />
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

        {/* Protected routes — canonical paths under /admin/*; legacy paths redirect for bookmarks */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/admin/dashboard" element={<Dashboard />} />
            <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/analytics" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/analytics" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/pipeline" element={<Pipeline />} />
            <Route path="/pipeline" element={<Navigate to="/admin/pipeline" replace />} />
            <Route path="/admin/candidates" element={<CandidatesList />} />
            <Route path="/candidates" element={<Navigate to="/admin/candidates" replace />} />
            <Route path="/admin/candidates/:id" element={<CandidateDetail />} />
            <Route path="/candidates/:id" element={<RedirectToAdminCandidate />} />
            <Route path="/admin/talent-pool" element={<TalentPool />} />
            <Route path="/talent-pool" element={<Navigate to="/admin/talent-pool" replace />} />
            <Route path="/admin/assessments" element={<Assessments />} />
            <Route path="/assessments" element={<Navigate to="/admin/assessments" replace />} />
            <Route path="/admin/settings" element={<Settings />} />
            <Route path="/settings" element={<Navigate to="/admin/settings" replace />} />
            <Route path="/admin/help" element={<HelpManuals />} />
            <Route path="/help" element={<Navigate to="/admin/help" replace />} />
          </Route>
        </Route>

        {/* HRM+ routes */}
        <Route element={<ProtectedRoute roles={['admin', 'hrm']} />}>
          <Route element={<AppLayout />}>
            <Route path="/admin/email-templates" element={<EmailTemplates />} />
            <Route path="/email-templates" element={<Navigate to="/admin/email-templates" replace />} />
            <Route path="/admin/career-settings" element={<CareerSettingsPage />} />
            <Route path="/career-settings" element={<Navigate to="/admin/career-settings" replace />} />
            <Route path="/admin/import" element={<ImportCandidates />} />
            <Route path="/import" element={<Navigate to="/admin/import" replace />} />
          </Route>
        </Route>

        {/* Admin only */}
        <Route element={<ProtectedRoute roles={['admin']} />}>
          <Route element={<AppLayout />}>
            <Route path="/admin/users" element={<Users />} />
            <Route path="/users" element={<Navigate to="/admin/users" replace />} />
            <Route path="/admin/roles" element={<RolesPermissions />} />
            <Route path="/roles" element={<Navigate to="/admin/roles" replace />} />
          </Route>
        </Route>

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
